import type { User as FirebaseUser } from 'firebase/auth'
import { useEffect, useState } from 'react'

interface AuthState {
  user: FirebaseUser | null | undefined
  loading: boolean
  error: Error | undefined
}

/**
 * Minimal replacement for react-firebase-hooks' `useAuthState` that defers
 * loading of the Firebase SDK until after the component mounts. Keeps
 * firebase/auth (~115 kB) out of the parent chunk's initial evaluation —
 * it still downloads, but no longer blocks render / hydration.
 */
export function useLazyFirebaseAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    user: undefined,
    loading: true,
    error: undefined
  })

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    let cancelled = false

    const init = async () => {
      try {
        // Ensures firebase.initializeApp() runs before getAuth().
        await import('src/firebase/firebaseClient')
        const { getAuth, onAuthStateChanged } = await import('firebase/auth')
        if (cancelled) return
        const auth = getAuth()
        unsubscribe = onAuthStateChanged(
          auth,
          (user) => {
            if (!cancelled) {
              setState({ user, loading: false, error: undefined })
            }
          },
          (error) => {
            if (!cancelled) {
              setState({
                user: null,
                loading: false,
                error: error as Error
              })
            }
          }
        )
      } catch (error) {
        if (!cancelled) {
          setState({
            user: null,
            loading: false,
            error: error instanceof Error ? error : new Error(String(error))
          })
        }
      }
    }

    init()

    return () => {
      cancelled = true
      if (unsubscribe) unsubscribe()
    }
  }, [])

  return state
}

export default useLazyFirebaseAuth
